import type { InvitationData } from '../types';
import type {
  BirthdayCustomData,
  BirthdayTimelineItem,
  BirthdaySnapshot,
  BirthdayCelebrant,
  BirthdayActivity,
  BirthdayFunFact,
  BirthdayWish,
  BirthdayMemoryCapsuleMessage,
  BirthdayStory,
  BirthdaySectionBackground,
} from '@/src/types';

export type {
  BirthdayTimelineItem,
  BirthdaySnapshot,
  BirthdayCelebrant,
  BirthdayActivity,
  BirthdayFunFact,
  BirthdayWish,
  BirthdayMemoryCapsuleMessage,
  BirthdayStory,
  BirthdaySectionBackground,
};

/** Legacy shape kept for backward-compat with existing invitations */
export interface HappyMember {
  name: string;
  photoUrl?: string;
  emoji?: string;
}

/** Legacy shape — schedule items saved before the `activities` field existed */
export interface LegacyScheduleItem {
  time: string;
  activity: string;
  venue?: string;
  description?: string;
  emoji?: string;
}

export interface BirthdayContent {
  hero: {
    birthdayPersonName: string;
    ageTurning?: string;
    tagline: string;
    eventDate: string;
    eventTime?: string;
    venueName?: string;
    hostNames: string;
    backgroundImageUrl?: string;
  };
  /** Kept for backward-compat with existing invitations that used photoTrio */
  photoTrio: { photos: string[] };
  /** New rich snapshot gallery (4-12 images with captions) */
  snapshots: BirthdaySnapshot[];
  /** New celebrants with relation + portrait style */
  celebrants: BirthdayCelebrant[];
  /** Legacy happy-members grid (shown only when celebrants are absent) */
  happyMembers: {
    label: string;
    title: string;
    members: HappyMember[];
  };
  /** New rich activity schedule */
  activities: BirthdayActivity[];
  /** Legacy schedule (shown when activities absent) */
  schedule: {
    label: string;
    title: string;
    items: LegacyScheduleItem[];
  };
  venue: {
    name: string;
    address: string;
    imageUrl?: string;
    mapsLink?: string;
    parkingInstructions?: string;
  };
  rsvp: {
    link?: string;
    message?: string;
  };
  timelineItems: BirthdayTimelineItem[];
  funFacts: BirthdayFunFact[];
  wishes: BirthdayWish[];
  memoryCapsuleMessages: BirthdayMemoryCapsuleMessage[];
  aiStory?: string;
  birthdayStory?: BirthdayStory;
  /** ISO string for countdown; undefined means section is hidden */
  eventIso?: string;
  /** Section visibility flags — undefined treated as true (default on) */
  showMemoryTimeline: boolean;
  showFunFacts: boolean;
  showBirthdayStory: boolean;
  showMemoryCapsule: boolean;
  /** Optional per-section background image overrides */
  sectionBackgrounds: NonNullable<BirthdayCustomData['sectionBackgrounds']>;
}

function fmt(iso: string): string {
  try {
    return new Intl.DateTimeFormat('en-IN', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

const DEFAULT_MEMBERS: HappyMember[] = [
  { name: 'Rahul', emoji: '🎉' },
  { name: 'Priya', emoji: '🎂' },
  { name: 'Amit', emoji: '🥳' },
  { name: 'Neha', emoji: '🎈' },
  { name: 'Ravi', emoji: '🎊' },
  { name: 'Sunita', emoji: '🎁' },
];

const DEFAULT_SCHEDULE: LegacyScheduleItem[] = [
  { time: '5:00 PM', activity: 'Arrive & Mingle', venue: 'Main Venue', emoji: '🚪' },
  { time: '7:00 PM', activity: 'Evening Snacks', venue: 'Garden Area', emoji: '🍿' },
  { time: '9:00 PM', activity: 'Dinner', venue: 'Mumbai Restaurant', emoji: '🍽️' },
  { time: '10:30 PM', activity: 'Dinner & Dance', venue: 'Dance Floor', emoji: '💃' },
];

export function resolveContent(data: InvitationData): BirthdayContent {
  const bd = (data.customData?.birthday ?? (data.customData as Record<string, unknown>)?.birthday) as BirthdayCustomData | undefined;

  const birthdayPersonName = bd?.birthdayPersonName ?? data.eventName;
  const tagline = bd?.celebrationTagline ?? `Celebrating ${birthdayPersonName}'s Special Day`;
  const hostNames = bd?.hostNames ?? data.hostName;

  const allMedia = data.mediaUrls ?? [];
  const [main, ...rest] = allMedia;
  const trioPhotos = rest.slice(0, 3);

  // Snapshots: prefer new `snapshots[]`, fall back to mediaUrls
  const snapshots: BirthdaySnapshot[] =
    bd?.snapshots?.length
      ? bd.snapshots
      : allMedia.slice(1).map((url) => ({ imageUrl: url, caption: '' }));

  // Celebrants: prefer new `celebrants[]`, fall back to legacy happyMembers
  const celebrants: BirthdayCelebrant[] =
    bd?.celebrants?.length
      ? bd.celebrants
      : (bd?.happyMembers ?? []).map((m) => ({
          name: m.name,
          imageUrl: m.photoUrl,
          relation: m.emoji,
        }));

  // Activities: prefer new `activities[]`, fall back to legacy `schedule`
  const activities: BirthdayActivity[] =
    bd?.activities?.length
      ? bd.activities
      : [];

  const legacyScheduleItems: LegacyScheduleItem[] =
    bd?.schedule?.length ? bd.schedule : DEFAULT_SCHEDULE;

  // Users commonly paste the venue name AND a maps link into one field, e.g.
  // "The Gazebo Banquet & https://share.google/abc". Pull the URL out from
  // anywhere in the string so we can: (a) render a working "Get Directions"
  // button, and (b) show only the readable name — never the raw URL — as text.
  const extractUrl = (s: string | undefined): { text: string; url?: string } => {
    if (!s) return { text: '' };
    const match = s.match(/https?:\/\/[^\s]+/i);
    if (!match) return { text: s.trim() };
    const url = match[0];
    const text = s
      .replace(url, '')
      .replace(/[\s&|,·•\-–—]+$/, '')
      .replace(/^[\s&|,·•\-–—]+/, '')
      .trim();
    return { text, url };
  };

  const nameParsed = extractUrl(bd?.venueName ?? data.eventVenue);
  const addressParsed = extractUrl(bd?.venueAddress ?? data.eventVenue);

  const resolvedMapsLink =
    bd?.mapsLink || nameParsed.url || addressParsed.url || undefined;

  const venueName = nameParsed.text;
  const venueAddress = addressParsed.text;

  return {
    hero: {
      birthdayPersonName,
      ageTurning: bd?.ageTurning,
      tagline,
      eventDate: fmt(data.eventDate),
      eventTime: bd?.eventTime,
      venueName,
      hostNames,
      backgroundImageUrl: bd?.heroBackgroundImageUrl ?? main,
    },
    photoTrio: { photos: trioPhotos },
    snapshots,
    celebrants,
    happyMembers: {
      label: 'The Squad',
      title: 'Happy Celebrants',
      members: bd?.happyMembers?.length ? bd.happyMembers : DEFAULT_MEMBERS,
    },
    activities,
    schedule: {
      label: 'The Plan',
      title: 'Activity Schedule',
      items: legacyScheduleItems,
    },
    venue: {
      name: venueName || 'Party Venue',
      address: venueAddress,
      imageUrl: bd?.venueImageUrl,
      mapsLink: resolvedMapsLink,
      parkingInstructions: bd?.parkingInstructions,
    },
    rsvp: {
      link: data.rsvpLink,
      message: data.customMessage,
    },
    timelineItems: bd?.timelineItems ?? [],
    funFacts: bd?.funFacts ?? [],
    wishes: bd?.wishes ?? [],
    memoryCapsuleMessages: bd?.memoryCapsuleMessages ?? [],
    aiStory: bd?.aiStory,
    birthdayStory: bd?.birthdayStory,
    eventIso: data.eventDate,
    // Visibility flags default to true (undefined = section is visible)
    showMemoryTimeline: bd?.showMemoryTimeline !== false,
    showFunFacts: bd?.showFunFacts !== false,
    showBirthdayStory: bd?.showBirthdayStory !== false,
    showMemoryCapsule: bd?.showMemoryCapsule !== false,
    sectionBackgrounds: bd?.sectionBackgrounds ?? {},
  };
}
